// vg-binding: P7.D-35
//
// apps/vendor/src/pages/Analytics.tsx
//
// Phase 7 Wave 12 Task 42 — Analytics page (MVP).
//
// UAT #2159/#2160 — date-range filter (7/14/30/90 ngày, BE đã hỗ trợ
// `?days=`) + nút xuất CSV (client-side từ data đã tải: timeseries +
// status buckets + top products).

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { formatCents } from '@printway/shared/currency';
import { PageHeader } from '@printway/ui';

import {
  useKpis,
  useOrdersByStatus,
  useTimeseries,
  useTopProducts,
} from '../features/analytics/api.js';
import {
  STATUS_PILL_CLASS,
  blueprintStatusToTone,
} from '../lib/ui/statusPill.js';

function fmtValue(value: number, unit: string): string {
  if (unit === 'cents') return formatCents(value);
  if (unit === '%') return `${value}%`;
  return String(value);
}

const STATUS_COLOR: Record<string, string> = {
  accepted: 'bg-yellow-300 dark:bg-yellow-500/60',
  in_production: 'bg-blue-300 dark:bg-blue-500/60',
  quality_check: 'bg-purple-300 dark:bg-purple-500/60',
  ready_to_ship: 'bg-orange-300 dark:bg-orange-500/60',
  shipped: 'bg-green-300 dark:bg-green-500/60',
  completed: 'bg-emerald-400 dark:bg-emerald-500/70',
};

// BE emits snake_case enum (apps/api/.../VendorAssignment.ts:66
// VENDOR_ASSIGNMENT_STATUSES); orders.statusLabel.* keys are camelCase.
// Map here so the donut bucket shows translated label, not raw key.
// Falls back to raw status string when unmapped.
const STATUS_LABEL_KEY: Record<string, string> = {
  assigned: 'assigned',
  accepted: 'accepted',
  in_production: 'inProduction',
  quality_check: 'qualityCheck',
  ready_to_ship: 'readyToShip',
  shipped: 'shipped',
  completed: 'completed',
  pending_reassignment: 'pendingReassignment',
  on_hold: 'onHold',
  rejected: 'rejected',
  superseded: 'superseded',
  cancelled: 'cancelled',
};

const RANGE_OPTIONS = [7, 14, 30, 90] as const;

/** RFC-4180-đủ-dùng: quote khi có dấu phẩy/quote/newline. */
function csvCell(v: string | number | null | undefined): string {
  const s = String(v ?? '');
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

export function AnalyticsPage(): JSX.Element {
  const { t } = useTranslation(['analytics', 'orders']);
  const [rangeDays, setRangeDays] = useState<number>(14);
  const kpisQ = useKpis();
  const tsQ = useTimeseries(rangeDays);
  const statusQ = useOrdersByStatus();
  const topQ = useTopProducts(5);

  const statusTotal = (statusQ.data?.buckets ?? []).reduce((s, b) => s + b.count, 0) || 1;

  // UAT #2160 — client-side CSV từ data đã tải (3 section, blank-line phân cách).
  function handleExportCsv(): void {
    const lines: string[] = [];
    lines.push(`# ${t('page.title')} — ${new Date().toISOString().slice(0, 10)} (${rangeDays}d)`);
    lines.push('');
    lines.push('section,date,value_cents');
    for (const p of tsQ.data?.rows ?? []) {
      lines.push(['revenue', csvCell(p.date), p.value].join(','));
    }
    lines.push('');
    lines.push('section,status,count');
    for (const b of statusQ.data?.buckets ?? []) {
      lines.push(['orders_by_status', csvCell(b.status), b.count].join(','));
    }
    lines.push('');
    lines.push('section,product,code,units_sold,orders,revenue_cents');
    for (const r of topQ.data?.rows ?? []) {
      lines.push(
        [
          'top_products',
          csvCell(r.name),
          csvCell(r.blueprint_code ?? r.sku ?? ''),
          r.units_sold,
          r.orders,
          r.revenue_cents,
        ].join(','),
      );
    }
    const blob = new Blob([`\uFEFF${lines.join('\n')}`], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `vendor-analytics-${Date.now()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <main
      data-testid="analytics-page-root"
      className="flex flex-col gap-4 px-6 py-6"
    >
      <PageHeader
        title={t('page.title')}
        subtitle={t('page.subtitle')}
        actions={
          <>
            {/* UAT #2159 — bộ lọc khoảng thời gian */}
            <div
              data-testid="analytics-range-filter"
              className="flex overflow-hidden rounded-lg border border-pw-border"
            >
              {RANGE_OPTIONS.map((d) => (
                <button
                  key={d}
                  type="button"
                  data-testid={`analytics-range-${d}`}
                  onClick={() => setRangeDays(d)}
                  className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                    rangeDays === d
                      ? 'bg-pw-primary text-white'
                      : 'bg-pw-bg text-pw-text-muted hover:bg-pw-surface'
                  }`}
                >
                  {t('range.days', { count: d })}
                </button>
              ))}
            </div>
            {/* UAT #2160 — xuất CSV */}
            <button
              type="button"
              data-testid="analytics-export-csv"
              onClick={handleExportCsv}
              disabled={tsQ.isLoading}
              className="rounded-lg border border-pw-border bg-pw-bg px-3 py-1.5 text-xs font-medium text-pw-text hover:bg-pw-surface disabled:opacity-50"
            >
              {t('export.csvButton')}
            </button>
          </>
        }
      />

      {/* KPI custom grid */}
      <section className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-4">
        {kpisQ.isLoading
          ? Array.from({ length: 4 }).map((_v, i) => (
              <div key={i} className="h-24 animate-pulse rounded-xl border border-pw-border bg-pw-surface" />
            ))
          : (kpisQ.data?.kpis ?? []).map((k) => {
              const isPlaceholder = k.data_source === 'placeholder';
              return (
                <div
                  key={k.id}
                  data-testid={`analytics-kpi-${k.id}`}
                  className="rounded-xl border border-pw-border bg-pw-bg p-5 shadow-sm"
                >
                  <p className="text-sm text-pw-text-muted">{k.label}</p>
                  {isPlaceholder ? (
                    <>
                      <p className="mt-2 text-2xl font-semibold text-pw-text-muted">
                        —
                      </p>
                      <p className="mt-1 text-xs text-pw-text-muted">
                        {t('kpi.noData')}
                      </p>
                    </>
                  ) : (
                    <p className="mt-2 text-2xl font-semibold text-pw-text">
                      {fmtValue(k.value, k.unit)}
                    </p>
                  )}
                </div>
              );
            })}
      </section>

      {/* Revenue line + status donut row */}
      <section className="grid grid-cols-1 gap-4 lg:grid-cols-[2fr_1fr]">
        <article
          data-testid="analytics-revenue-line"
          className="rounded-xl border border-pw-border bg-pw-bg p-5 shadow-sm"
        >
          <h3 className="mb-3 text-sm font-semibold text-pw-text">{t('revenueLine.title')}</h3>
          {tsQ.isLoading ? (
            <div className="h-40 animate-pulse rounded-md bg-pw-surface" />
          ) : tsQ.data?.data_source === 'placeholder' ||
            (tsQ.data?.rows ?? []).length === 0 ? (
            <p className="rounded-md border border-dashed border-pw-border bg-pw-surface px-3 py-8 text-center text-sm text-pw-text-muted">
              {t('revenueLine.empty')}
            </p>
          ) : (
            // UAT #2153 — inline divs chỉ có native title (delay ~1s, tester
            // tưởng không có tooltip). Recharts tooltip hiện tức thì khi hover.
            <ResponsiveContainer width="100%" height={200}>
              <BarChart
                data={tsQ.data!.rows}
                margin={{ top: 4, right: 4, bottom: 0, left: 4 }}
              >
                <CartesianGrid vertical={false} stroke="var(--pw-border)" strokeDasharray="3 3" />
                <XAxis
                  dataKey="date"
                  tickFormatter={(d: string) => d.slice(5)}
                  tick={{ fill: 'var(--pw-text-muted)', fontSize: 11 }}
                  axisLine={{ stroke: 'var(--pw-border)' }}
                  tickLine={false}
                  interval="preserveStartEnd"
                />
                <YAxis
                  tickFormatter={(v: number) => formatCents(v)}
                  tick={{ fill: 'var(--pw-text-muted)', fontSize: 11 }}
                  axisLine={false}
                  tickLine={false}
                  width={64}
                />
                <Tooltip
                  cursor={{ fill: 'var(--pw-surface)' }}
                  content={({ active, payload, label }) =>
                    active && payload?.length ? (
                      <div className="rounded-md border border-pw-border bg-pw-bg px-3 py-2 text-xs shadow-md">
                        <p className="text-pw-text-muted">{String(label)}</p>
                        <p className="font-semibold text-pw-text">
                          {formatCents(Number(payload[0]?.value ?? 0))}
                        </p>
                      </div>
                    ) : null
                  }
                />
                <Bar dataKey="value" fill="var(--pw-primary-bg)" radius={[4, 4, 0, 0]} maxBarSize={40} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </article>

        <article
          data-testid="analytics-status-donut"
          className="rounded-xl border border-pw-border bg-pw-bg p-5 shadow-sm"
        >
          <h3 className="mb-3 text-sm font-semibold text-pw-text">{t('statusDonut.title')}</h3>
          {statusQ.data?.data_source === 'placeholder' ||
          (statusQ.data?.buckets ?? []).length === 0 ? (
            <p className="rounded-md border border-dashed border-pw-border bg-pw-surface px-3 py-8 text-center text-sm text-pw-text-muted">
              {t('statusDonut.empty')}
            </p>
          ) : (
            <ul className="flex flex-col gap-2">
              {statusQ.data!.buckets.map((b) => {
                const pct = Math.round((b.count / statusTotal) * 100);
                return (
                  <li key={b.status} className="text-xs">
                    <div className="flex items-center justify-between">
                      <span className="text-pw-text-muted">
                        {t(
                          `orders:statusLabel.${STATUS_LABEL_KEY[b.status] ?? b.status}`,
                          { defaultValue: b.status },
                        )}
                      </span>
                      <span className="font-mono text-pw-text">
                        {b.count} ({pct}%)
                      </span>
                    </div>
                    <div className="mt-1 h-2 w-full overflow-hidden rounded-full bg-pw-surface">
                      <div
                        className={`h-full ${STATUS_COLOR[b.status] ?? 'bg-pw-primary/40'}`}
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </article>
      </section>

      {/* Top products */}
      <section
        data-testid="analytics-top-products"
        className="rounded-xl border border-pw-border bg-pw-bg p-5 shadow-sm"
      >
        <h3 className="mb-3 text-sm font-semibold text-pw-text">{t('topProducts.title')}</h3>
        {topQ.data?.data_source === 'placeholder' ||
        (topQ.data?.rows ?? []).length === 0 ? (
          <p className="rounded-md border border-dashed border-pw-border bg-pw-surface px-3 py-8 text-center text-sm text-pw-text-muted">
            {t('topProducts.empty')}
          </p>
        ) : (
          <div className="overflow-x-auto">
          <table className="w-full table-auto text-sm">
            <thead className="border-b border-pw-border text-xs uppercase tracking-wider text-pw-text-muted">
              <tr>
                <th className="px-3 py-2 text-left">{t('topProducts.columnProduct')}</th>
                <th className="px-3 py-2 text-left">{t('topProducts.columnCode')}</th>
                <th className="px-3 py-2 text-left">{t('topProducts.columnCategory')}</th>
                <th className="px-3 py-2 text-left">{t('topProducts.columnStatus')}</th>
                <th className="px-3 py-2 text-right">{t('topProducts.columnUnits')}</th>
                <th className="px-3 py-2 text-right">{t('topProducts.columnOrders')}</th>
                <th className="px-3 py-2 text-right">{t('topProducts.columnRevenue')}</th>
              </tr>
            </thead>
            <tbody>
              {topQ.data!.rows.map((r) => {
                const statusBadge = STATUS_PILL_CLASS[blueprintStatusToTone(r.status)];
                return (
                  <tr key={r.product_id} className="border-b border-pw-border">
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-3">
                        {r.image_url ? (
                          <img
                            src={r.image_url}
                            alt={r.name}
                            loading="lazy"
                            className="h-10 w-10 flex-none rounded-md border border-pw-border object-cover"
                          />
                        ) : (
                          <div className="flex h-10 w-10 flex-none items-center justify-center rounded-md border border-dashed border-pw-border bg-pw-surface text-[10px] text-pw-text-muted">
                            {r.sku ?? '—'}
                          </div>
                        )}
                        <div className="min-w-0">
                          <div className="truncate font-medium text-pw-text">{r.name}</div>
                          <div className="truncate text-xs text-pw-text-muted">
                            {r.slug ?? `#${r.product_id.slice(-8)}`}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-2 align-middle">
                      <span className="font-mono text-xs text-pw-text-muted">
                        {r.blueprint_code ?? r.sku ?? '—'}
                      </span>
                    </td>
                    <td className="px-3 py-2 align-middle text-pw-text-muted">
                      {r.category ?? '—'}
                    </td>
                    <td className="px-3 py-2 align-middle">
                      {r.status ? (
                        <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${statusBadge}`}>
                          {r.status}
                        </span>
                      ) : (
                        <span className="text-pw-text-muted">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right align-middle font-mono">{r.units_sold}</td>
                    <td className="px-3 py-2 text-right align-middle">{r.orders}</td>
                    <td className="px-3 py-2 text-right align-middle">{formatCents(r.revenue_cents)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
        )}
      </section>
    </main>
  );
}

export default AnalyticsPage;
